"use client";

import { Button } from "@ui/components/core/Button";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";

interface ConvAITermsModalProps {
  onAgree: () => void;
  onClose: () => void;
}

export function ConvAITerms({ onAgree, onClose }: ConvAITermsModalProps) {
  const t = useTranslations("ClientKeys.hero_video_agent");

  return (
    <>
      <p className='tw-type-base tw-mb-6 tw-text-pretty tw-pr-8 tw-text-black'>{t("terms.intro")}</p>

      <ul role='list' className='tw-mb-6 tw-space-y-3 tw-text-xs/xs tw-text-gray-500'>
        <li className='tw-gap-2.5 tw-flex tw-items-start'>
          <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
          <span>{t("terms.age_requirement")}</span>
        </li>
        <li className='tw-gap-2.5 tw-flex tw-items-start'>
          <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
          <span>{t("terms.ai_acknowledgement")}</span>
        </li>
        <li className='tw-gap-2.5 tw-flex tw-items-start'>
          <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
          <span>{t("terms.recording_consent")}</span>
        </li>
        <li className='tw-gap-2.5 tw-flex tw-items-start'>
          <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
          <span>
            {t.rich("terms.privacy_policy", {
              link: chunks => (
                <a
                  href='https://elevenlabs.io/privacy-policy'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='tw-text-black tw-underline tw-underline-offset-2 hover:tw-no-underline'
                >
                  {chunks}
                </a>
              ),
            })}
          </span>
        </li>
        <li className='tw-gap-2.5 tw-flex tw-items-start'>
          <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
          <span>{t("terms.data_sharing_consent")}</span>
        </li>
      </ul>

      <div className='tw-grid tw-grid-cols-2 tw-gap-2 tw-pb-1'>
        <Button onClick={onClose} variant='elevated' size='lg' className='tw-w-full'>
          {t("terms.cancel_button")}
        </Button>
        <Button onClick={onAgree} variant='primary' size='lg' className='tw-w-full'>
          {t("terms.agree_button")}
        </Button>
      </div>
    </>
  );
}

export function ConvAITermsModal({ onAgree, onClose }: ConvAITermsModalProps) {
  const t = useTranslations("ClientKeys.hero_video_agent");

  return (
    <div className='bg-black/60 fixed inset-0 z-50 flex items-center justify-center px-16 backdrop-blur-sm'>
      <div
        className='relative flex w-full max-w-[460px] flex-col rounded-[24px] bg-default p-28'
        style={{
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.2)",
        }}
      >
        <button
          className='absolute right-16 top-16 flex h-32 w-32 items-center justify-center rounded-full text-light transition-colors hover:bg-overlay-dark hover:text-default'
          onClick={onClose}
          aria-label={t("terms.close")}
        >
          <XIcon size={18} />
        </button>

        <ConvAITerms onAgree={onAgree} onClose={onClose} />
      </div>
    </div>
  );
}
